<!-- resources/js/views/agents/AgentFileManager.vue -->
<template>
  <div class="agent-file-manager">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>فایل‌های مرجع</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        <i class="fas fa-upload"></i> آپلود فایل
      </button>
    </div>

    <!-- فیلترها -->
    <div class="filters mb-4">
      <input v-model="search" placeholder="جستجو در فایل‌ها..." class="form-control" @input="loadFiles" />
      <select v-model="categoryFilter" class="form-control" @change="loadFiles">
        <option value="">همه دسته‌بندی‌ها</option>
        <option value="seo">سئو</option>
        <option value="brand">برند</option>
        <option value="template">قالب</option>
        <option value="product">محصولات</option>
        <option value="customer">مشتریان</option>
        <option value="other">سایر</option>
      </select>
    </div>

    <!-- لیست فایل‌ها -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary"></div>
      <p>در حال بارگذاری...</p>
    </div>

    <div v-else-if="files.length === 0" class="text-center py-4">
      <p class="text-muted">هیچ فایلی یافت نشد</p>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-striped">
        <thead>
        <tr>
          <th>#</th>
          <th>نام</th>
          <th>توضیحات</th>
          <th>دسته‌بندی</th>
          <th>نوع</th>
          <th>وضعیت</th>
          <th>عملیات</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="file in files" :key="file.id">
          <td>{{ file.id }}</td>
          <td>{{ file.name }}</td>
          <td>{{ truncate(file.description, 40) }}</td>
          <td>{{ getCategoryLabel(file.category) }}</td>
          <td><span class="badge badge-info">{{ file.file_type.toUpperCase() }}</span></td>
          <td>
              <span class="badge" :class="file.is_active ? 'badge-success' : 'badge-secondary'">
                {{ file.is_active ? 'فعال' : 'غیرفعال' }}
              </span>
          </td>
          <td>
            <button @click="previewFile(file)" class="btn btn-sm btn-info">پیش‌نمایش</button>
            <button @click="downloadFile(file)" class="btn btn-sm btn-primary">دانلود</button>
            <button @click="toggleFile(file)" class="btn btn-sm" :class="file.is_active ? 'btn-secondary' : 'btn-success'">
              {{ file.is_active ? 'غیرفعال' : 'فعال' }}
            </button>
            <button @click="deleteFile(file)" class="btn btn-sm btn-danger">حذف</button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- مودال آپلود -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h5>آپلود فایل جدید</h5>
          <button @click="showCreateModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="uploadFile">
            <div class="form-group">
              <label>نام فایل</label>
              <input v-model="newFile.name" type="text" class="form-control" required />
            </div>
            <div class="form-group">
              <label>توضیحات</label>
              <textarea v-model="newFile.description" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>دسته‌بندی</label>
              <select v-model="newFile.category" class="form-control">
                <option value="seo">سئو</option>
                <option value="brand">برند</option>
                <option value="template">قالب</option>
                <option value="product">محصولات</option>
                <option value="customer">مشتریان</option>
                <option value="other">سایر</option>
              </select>
            </div>
            <div class="form-group">
              <label>فایل</label>
              <input type="file" ref="fileInput" @change="handleFileSelect" class="form-control" accept=".json,.txt,.csv,.pdf,.doc,.docx" required />
            </div>
            <button type="submit" class="btn btn-primary btn-block" :disabled="uploading">
              {{ uploading ? 'در حال آپلود...' : 'آپلود فایل' }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- مودال پیش‌نمایش -->
    <div v-if="showPreviewModal" class="modal-overlay" @click.self="showPreviewModal = false">
      <div class="modal-content modal-lg">
        <div class="modal-header">
          <h5>پیش‌نمایش: {{ previewFileData ? previewFileData.name : '' }}</h5>
          <button @click="showPreviewModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="previewLoading" class="text-center py-4">
            <div class="spinner-border text-primary"></div>
            <p>در حال بارگذاری...</p>
          </div>
          <pre v-else-if="previewContent" class="preview-content">{{ previewContent }}</pre>
          <p v-else class="text-muted text-center">محتوایی برای نمایش وجود ندارد.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AgentFileManager',
  data() {
    return {
      files: [],
      search: '',
      categoryFilter: '',
      showCreateModal: false,
      showPreviewModal: false,
      uploading: false,
      loading: false,
      previewLoading: false,
      previewFileData: null,
      previewContent: '',
      newFile: {
        name: '',
        description: '',
        category: 'other',
        file: null
      }
    }
  },
  mounted() {
    this.loadFiles();
  },
  methods: {
    async loadFiles() {
      this.loading = true;
      try {
        const params = {};
        if (this.search) params.search = this.search;
        if (this.categoryFilter) params.category = this.categoryFilter;

        const response = await axios.get('/api/admin/agent-files', { params });
        this.files = response.data;
      } catch (error) {
        console.error('Error loading files:', error);
        this.$toast?.error('خطا در بارگذاری فایل‌ها');
      } finally {
        this.loading = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.newFile.file = file;
        if (!this.newFile.name) {
          this.newFile.name = file.name.replace(/\.[^/.]+$/, '');
        }
      }
    },

    async uploadFile() {
      if (!this.newFile.file) {
        this.$toast?.error('لطفاً یک فایل انتخاب کنید');
        return;
      }

      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append('name', this.newFile.name);
        formData.append('description', this.newFile.description || '');
        formData.append('category', this.newFile.category);
        formData.append('file', this.newFile.file);

        await axios.post('/api/admin/agent-files', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        await this.loadFiles();
        this.showCreateModal = false;
        this.newFile = { name: '', description: '', category: 'other', file: null };
        this.$refs.fileInput.value = '';
        this.$toast?.success('فایل با موفقیت آپلود شد');
      } catch (error) {
        console.error('Error uploading file:', error);
        this.$toast?.error('خطا در آپلود فایل');
      } finally {
        this.uploading = false;
      }
    },

    async toggleFile(file) {
      try {
        await axios.post(`/api/admin/agent-files/${file.id}/toggle`);
        await this.loadFiles();
        this.$toast?.success('وضعیت فایل تغییر کرد');
      } catch (error) {
        console.error('Error toggling file:', error);
        this.$toast?.error('خطا در تغییر وضعیت');
      }
    },

    async deleteFile(file) {
      if (!confirm(`آیا از حذف فایل "${file.name}" مطمئن هستید؟`)) return;

      try {
        await axios.delete(`/api/admin/agent-files/${file.id}`);
        await this.loadFiles();
        this.$toast?.success('فایل حذف شد');
      } catch (error) {
        console.error('Error deleting file:', error);
        this.$toast?.error('خطا در حذف فایل');
      }
    },

    async downloadFile(file) {
      try {
        window.open(`/api/admin/agent-files/${file.id}/download`, '_blank');
      } catch (error) {
        console.error('Error downloading file:', error);
        this.$toast?.error('خطا در دانلود فایل');
      }
    },

    async previewFile(file) {
      this.previewFileData = file;
      this.previewContent = '';
      this.previewLoading = true;
      this.showPreviewModal = true;

      try {
        const response = await axios.get(`/api/admin/agent-files/${file.id}/preview`);
        const data = response.data;

        if (typeof data === 'object') {
          this.previewContent = JSON.stringify(data, null, 2);
        } else if (data.content) {
          this.previewContent = data.content;
        } else {
          this.previewContent = JSON.stringify(data, null, 2);
        }
      } catch (error) {
        console.error('Error previewing file:', error);
        this.previewContent = 'خطا در بارگذاری محتوای فایل';
      } finally {
        this.previewLoading = false;
      }
    },

    getCategoryLabel(category) {
      const labels = {
        'seo': 'سئو',
        'brand': 'برند',
        'template': 'قالب',
        'product': 'محصولات',
        'customer': 'مشتریان',
        'other': 'سایر'
      };
      return labels[category] || category;
    },

    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    }
  }
}
</script>

<style scoped>
.agent-file-manager {
  padding: 20px;
}

.filters {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.filters .form-control {
  flex: 1;
  min-width: 200px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 500px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 0;
}

.modal-content.modal-lg {
  width: 800px;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h5 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #9ca3af;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-sm {
  padding: 4px 10px;
  font-size: 12px;
  margin: 0 2px;
}

.btn-block {
  width: 100%;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
}

.badge-success { background: #28a745; }
.badge-secondary { background: #6c757d; }
.badge-info { background: #17a2b8; }

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  text-align: right;
}

.table th {
  background: #f8f9fa;
  font-weight: 600;
}

.table-striped tbody tr:nth-child(odd) {
  background: #f8f9fa;
}

.table-responsive {
  overflow-x: auto;
}

.preview-content {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  max-height: 500px;
  overflow-y: auto;
  font-size: 13px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.text-center { text-align: center; }
.text-muted { color: #6c757d; }
.py-4 { padding: 40px 0; }
.mb-4 { margin-bottom: 20px; }
.mt-3 { margin-top: 15px; }

.spinner-border {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>